import { DndContext, DragOverlay, UniqueIdentifier, closestCenter } from '@dnd-kit/core';
import { restrictToFirstScrollableAncestor } from '@dnd-kit/modifiers';
import {
  SortableContext as DndSortableContext,
  horizontalListSortingStrategy,
  rectSortingStrategy,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import React from 'react';
import { OnDragEndParams, OverlayComponentParams, SortableStrategy } from './dragAndDrop.types';
import useSortableContext from './useSortableContext';

type SortableContextProps<T extends object> = {
  items: T[];
  uniqueIdentifier: keyof T;
  onDragEnd?: (props: OnDragEndParams) => void;
  onUpdateItems: (newItems: T[]) => void;
  children: React.ReactNode;
  OverlayComponent: (params: OverlayComponentParams) => React.ReactElement;
  strategy: SortableStrategy;
};

const SortableContext = <T extends object>({
  items,
  onDragEnd,
  onUpdateItems,
  uniqueIdentifier,
  children,
  OverlayComponent,
  strategy,
}: SortableContextProps<T>) => {
  const {
    activeId,
    onDragEnd: handleDragEnd,
    onDragStart,
    onDragCancel,
    sensors,
    activeIndex,
  } = useSortableContext({
    items,
    onDragEnd,
    onUpdateItems,
    uniqueIdentifier,
  });

  const strategyParams = {
    [SortableStrategy.GRID]: rectSortingStrategy,
    [SortableStrategy.VERTICAL]: verticalListSortingStrategy,
    [SortableStrategy.HORIZONTAL]: horizontalListSortingStrategy,
  };

  return (
    <DndContext
      sensors={sensors}
      onDragStart={onDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={onDragCancel}
      collisionDetection={closestCenter}
      modifiers={[restrictToFirstScrollableAncestor]}
    >
      <DndSortableContext
        items={items as (T & { id: UniqueIdentifier })[]}
        strategy={strategyParams[strategy]}
      >
        {children}
      </DndSortableContext>
      <DragOverlay adjustScale={false} dropAnimation={null}>
        {activeId && activeIndex >= 0 ? (
          <OverlayComponent activeId={activeId} activeIndex={activeIndex} />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

export default SortableContext;
